/**
 * KNN-PCA surface-normal estimation — the portable equivalent of taman-ml's
 * Open3D `PointCloud.estimate_normals(KDTreeSearchParamRadius(radius=2.0))`
 * (see `taman-ml/taman/util/open3d/point_sampling.py`).
 *
 * For every sampled grid point we gather all neighbours within `radius` metres
 * (the query point itself included, exactly like Open3D's radius search), build
 * the 3×3 covariance matrix of that neighbourhood and take the eigenvector of
 * the SMALLEST eigenvalue as the surface normal. This matches Open3D bit-for-bit
 * up to the eigensolver's floating-point noise.
 *
 * WHY THIS MATTERS: the angle of incidence ε (and therefore the horizontal
 * illuminance E = I·f_M·cos³ε / h²) is dominated by the surface normal. A golden
 * comparison against ml's `measurement.json` showed triangle normals give a
 * ~2.4 % median E error, while these KNN-PCA normals bring it to ~0.25 %
 * (100 % of points within 1 %). The residual is ml's 2-decimal IES export.
 *
 * The normal SIGN is irrelevant downstream: `get_angle_of_incidence`
 * (vector_jax.py) flips the normal toward the light (`dot(toLight, n) < 0 → -n`),
 * so only the eigenvector's line (direction up to sign) matters — which is
 * exactly what Open3D's un-oriented `estimate_normals` returns too.
 *
 * Pure + dependency-free so it runs inside the measurement worker.
 */
import type { Vec3 } from './types';

/** taman-ml `SEARCH_PARAM_RADIUS = KDTreeSearchParamRadius(radius=2.0)`. */
export const KNN_NORMAL_RADIUS_M = 2.0;

/** Open3D's fallback normal when a neighbourhood is degenerate (< 3 points). */
const DEFAULT_NORMAL: Vec3 = [0, 0, 1];

/**
 * Smallest-eigenvalue eigenvector of a symmetric 3×3 matrix via cyclic Jacobi
 * rotations — the same family of solver Open3D uses (SelfAdjointEigenSolver).
 * `m` is given by its 6 unique entries. Returns a unit eigenvector.
 */
const smallestEigenvector = (a00: number, a01: number, a02: number, a11: number, a12: number, a22: number): Vec3 => {
  // Working copy of the symmetric matrix.
  let m00 = a00;
  let m01 = a01;
  let m02 = a02;
  let m11 = a11;
  let m12 = a12;
  let m22 = a22;
  // Accumulated eigenvector basis (columns are eigenvectors).
  let v00 = 1;
  let v01 = 0;
  let v02 = 0;
  let v10 = 0;
  let v11 = 1;
  let v12 = 0;
  let v20 = 0;
  let v21 = 0;
  let v22 = 1;

  for (let sweep = 0; sweep < 50; sweep += 1) {
    const off = Math.abs(m01) + Math.abs(m02) + Math.abs(m12);
    if (off < 1e-14) break;

    // Rotate to zero each off-diagonal entry in turn: (0,1), (0,2), (1,2).
    // --- (p,q) = (0,1) ---
    if (Math.abs(m01) > 1e-300) {
      const theta = (m11 - m00) / (2 * m01);
      const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
      const c = 1 / Math.sqrt(t * t + 1);
      const s = t * c;
      const newM00 = c * c * m00 - 2 * s * c * m01 + s * s * m11;
      const newM11 = s * s * m00 + 2 * s * c * m01 + c * c * m11;
      const newM02 = c * m02 - s * m12;
      const newM12 = s * m02 + c * m12;
      m00 = newM00;
      m11 = newM11;
      m01 = 0;
      m02 = newM02;
      m12 = newM12;
      for (let k = 0; k < 3; k += 1) {
        const vk0 = k === 0 ? v00 : k === 1 ? v10 : v20;
        const vk1 = k === 0 ? v01 : k === 1 ? v11 : v21;
        const nv0 = c * vk0 - s * vk1;
        const nv1 = s * vk0 + c * vk1;
        if (k === 0) {
          v00 = nv0;
          v01 = nv1;
        } else if (k === 1) {
          v10 = nv0;
          v11 = nv1;
        } else {
          v20 = nv0;
          v21 = nv1;
        }
      }
    }
    // --- (p,q) = (0,2) ---
    if (Math.abs(m02) > 1e-300) {
      const theta = (m22 - m00) / (2 * m02);
      const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
      const c = 1 / Math.sqrt(t * t + 1);
      const s = t * c;
      const newM00 = c * c * m00 - 2 * s * c * m02 + s * s * m22;
      const newM22 = s * s * m00 + 2 * s * c * m02 + c * c * m22;
      const newM01 = c * m01 - s * m12;
      const newM12 = s * m01 + c * m12;
      m00 = newM00;
      m22 = newM22;
      m02 = 0;
      m01 = newM01;
      m12 = newM12;
      for (let k = 0; k < 3; k += 1) {
        const vk0 = k === 0 ? v00 : k === 1 ? v10 : v20;
        const vk2 = k === 0 ? v02 : k === 1 ? v12 : v22;
        const nv0 = c * vk0 - s * vk2;
        const nv2 = s * vk0 + c * vk2;
        if (k === 0) {
          v00 = nv0;
          v02 = nv2;
        } else if (k === 1) {
          v10 = nv0;
          v12 = nv2;
        } else {
          v20 = nv0;
          v22 = nv2;
        }
      }
    }
    // --- (p,q) = (1,2) ---
    if (Math.abs(m12) > 1e-300) {
      const theta = (m22 - m11) / (2 * m12);
      const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
      const c = 1 / Math.sqrt(t * t + 1);
      const s = t * c;
      const newM11 = c * c * m11 - 2 * s * c * m12 + s * s * m22;
      const newM22 = s * s * m11 + 2 * s * c * m12 + c * c * m22;
      const newM01 = c * m01 - s * m02;
      const newM02 = s * m01 + c * m02;
      m11 = newM11;
      m22 = newM22;
      m12 = 0;
      m01 = newM01;
      m02 = newM02;
      for (let k = 0; k < 3; k += 1) {
        const vk1 = k === 0 ? v01 : k === 1 ? v11 : v21;
        const vk2 = k === 0 ? v02 : k === 1 ? v12 : v22;
        const nv1 = c * vk1 - s * vk2;
        const nv2 = s * vk1 + c * vk2;
        if (k === 0) {
          v01 = nv1;
          v02 = nv2;
        } else if (k === 1) {
          v11 = nv1;
          v12 = nv2;
        } else {
          v21 = nv1;
          v22 = nv2;
        }
      }
    }
  }

  // Diagonal now holds the eigenvalues; columns of V the eigenvectors. Pick the
  // column for the smallest eigenvalue (= surface normal).
  const eigenvalues = [m00, m11, m22];
  let minIdx = 0;
  if (eigenvalues[1] < eigenvalues[minIdx]) minIdx = 1;
  if (eigenvalues[2] < eigenvalues[minIdx]) minIdx = 2;
  const n: Vec3 = minIdx === 0 ? [v00, v10, v20] : minIdx === 1 ? [v01, v11, v21] : [v02, v12, v22];
  const len = Math.hypot(n[0], n[1], n[2]) || 1;
  return [n[0] / len, n[1] / len, n[2] / len];
};

/**
 * Spatial hash bucketing points into `radius`-sized cubic cells for O(1) lookup.
 * 17 bits per axis (stride 2¹⁷ = 131072, bias 2¹⁶ = 65536 → cell indices in
 * [−65536, 65535]); max key ≈ 2⁵¹ stays inside Number.MAX_SAFE_INTEGER (2⁵³).
 * A scene whose extent / radius exceeds ±65536 cells would alias — far beyond
 * any real lighting scene.
 */
const CELL_BIAS = 65536;
const CELL_STRIDE = 131072;
const cellKey = (ix: number, iy: number, iz: number): number =>
  ix + CELL_BIAS + (iy + CELL_BIAS) * CELL_STRIDE + (iz + CELL_BIAS) * CELL_STRIDE * CELL_STRIDE;

/**
 * Estimate per-point surface normals from the point set itself (KNN-PCA),
 * matching ml's `estimate_normals(KDTreeSearchParamRadius(radius))`.
 *
 * @param points  sampled grid points (one layer's worth — ml estimates per mesh)
 * @param radius  neighbourhood radius in metres (ml: 2.0)
 */
export const estimateNormalsKnn = (points: ReadonlyArray<Vec3>, radius = KNN_NORMAL_RADIUS_M): Vec3[] => {
  const n = points.length;
  const normals: Vec3[] = new Array(n);
  if (n === 0) return normals;

  const r2 = radius * radius;
  const inv = 1 / radius;

  // Build the spatial hash.
  const buckets = new Map<number, number[]>();
  for (let i = 0; i < n; i += 1) {
    const p = points[i];
    const key = cellKey(Math.floor(p[0] * inv), Math.floor(p[1] * inv), Math.floor(p[2] * inv));
    const bucket = buckets.get(key);
    if (bucket) bucket.push(i);
    else buckets.set(key, [i]);
  }

  for (let i = 0; i < n; i += 1) {
    const p = points[i];
    const cx = Math.floor(p[0] * inv);
    const cy = Math.floor(p[1] * inv);
    const cz = Math.floor(p[2] * inv);

    // Accumulate covariance over the 27 neighbouring cells.
    let cnt = 0;
    let sx = 0;
    let sy = 0;
    let sz = 0;
    let sxx = 0;
    let sxy = 0;
    let sxz = 0;
    let syy = 0;
    let syz = 0;
    let szz = 0;
    for (let dx = -1; dx <= 1; dx += 1) {
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dz = -1; dz <= 1; dz += 1) {
          const bucket = buckets.get(cellKey(cx + dx, cy + dy, cz + dz));
          if (!bucket) continue;
          for (const j of bucket) {
            const q = points[j];
            const ddx = q[0] - p[0];
            const ddy = q[1] - p[1];
            const ddz = q[2] - p[2];
            if (ddx * ddx + ddy * ddy + ddz * ddz > r2) continue;
            cnt += 1;
            sx += q[0];
            sy += q[1];
            sz += q[2];
            sxx += q[0] * q[0];
            sxy += q[0] * q[1];
            sxz += q[0] * q[2];
            syy += q[1] * q[1];
            syz += q[1] * q[2];
            szz += q[2] * q[2];
          }
        }
      }
    }

    if (cnt < 3) {
      normals[i] = DEFAULT_NORMAL;
      continue;
    }

    // Covariance = E[xx] - E[x]E[x] (Open3D normalises by neighbour count).
    const mx = sx / cnt;
    const my = sy / cnt;
    const mz = sz / cnt;
    const c00 = sxx / cnt - mx * mx;
    const c01 = sxy / cnt - mx * my;
    const c02 = sxz / cnt - mx * mz;
    const c11 = syy / cnt - my * my;
    const c12 = syz / cnt - my * mz;
    const c22 = szz / cnt - mz * mz;

    normals[i] = smallestEigenvector(c00, c01, c02, c11, c12, c22);
  }

  return normals;
};
